import { femaleQuestionMedium, maleQuestionMedium } from '@/assets/icons';
import {
  HAVING_NO_PROBLEMS,
  HAVING_NO_PROBLEMS_SOURCE,
  WASTE,
  WASTE_SOURCE,
  WITHOUT_DATA,
  WITHOUT_DATA_SOURCE,
} from './quotes';

const WHAT_IS_A_PB = `Un problème est un <strong>écart</strong> entre la situation <strong>actuelle</strong> et la situation <strong>idéale</strong>.`;

const SPOT_PB = `
<div class="ia-sensei-quote">
  <span class="quote">"${HAVING_NO_PROBLEMS}"</span>
  <br>
  <span class="author">${HAVING_NO_PROBLEMS_SOURCE}</span>
</div>
<h2 class="basic-sub-title">
  Qu'est-ce qu'un problème ?
</h2>
<p>
  Revenons à l'essentiel un moment :<br>
  @:sensei.WHAT_IS_A_PB<br>
  La situation idéale peut être un standard ou un objectif d'indicateur par exemple.
  En d'autres termes, un problème est une opportunité d'améliorer la situation actuelle et de rendre quelque chose plus efficace.<br>
  Le suivi quotidien des indicateurs de performance (ou KPI) est un excellent point de départ pour repérer les problèmes.
  Un KPI qui dépasse l'objectif est synonyme de problème car il y a un écart visible entre la ligne d'objectif et les valeurs de dépassement.
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Ok, mais ne pas avoir de problèmes signifierait que la situation est la meilleure possible, non ?</span>
</div>
<p>
  Ou que les gens devraient y regarder de plus près. Regardez autour de vous et posez-vous ces questions <i>"Chaque chose, équipement, machine est-elle dans son état idéal ?"</i> ou <i>"Y a-t-il quelque chose qui pourrait être amélioré dans ma routine de travail ?"</i><br>
</p>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${maleQuestionMedium}"></path></svg>
  <span class="question">Et si je pense que quelque chose pourrait être amélioré, mais que je sens que ce n'est pas à moi de le mentionner ?</span>
</div>
<p>
  La réponse est simple : Utilisez des <strong>données chiffrées</strong>. Lorsque vous remontez un problème au sein de Shizen,
  vous avez la possibilité de remplir plusieurs champs. Le champ "Combien" est en fait un rappel qu'il faut toujours utiliser des données précises pour définir un problème.<br>
  Rappelons qu'un problème est un écart entre la situation actuelle et une situation idéale : Cela signifie que
  toute personne qui lit la description d'un problème doit être en mesure de comprendre l'ampleur de l'écart entre la situation idéale et la situation actuelle.<br>
  Pour cela, il faut des données.
</p>
<div class="ia-sensei-quote">
  <span class="quote">"${WITHOUT_DATA}"</span>
  <br>
  <span class="author">${WITHOUT_DATA_SOURCE}</span>
</div>
<h2 class="basic-sub-title">
  Types de gaspillage
</h2>
<p>
  Comprendre les principaux types de gaspillage peut vous aider à identifier les problèmes.<br>
  Nous appelons ces gaspillages "Muda", il en existe 7 grands types :
  <ul>
    <li>
      Surproduction : C'est-à-dire produire en trop grande quantité ou au mauvais moment. Ceci entraine nécessairement des stocks importants, donc du gaspillage d'espace.
    </li>
    <li>
      Attente : Celui-ci apparaît lorsqu'un collaborateur doit attendre le résultat d'une machine par exemple. On peut alors supposer que le \"flux\" peut être optimisé, c'est-à-dire que l'on peut repenser l'enchaînement des étapes du processus.
    </li>
    <li>
      Transport : Un muda de transport vient du fait que des zones en relation sont trop éloignées. Par exemple, l'atelier A produit un ensemble de pièces à envoyer à un atelier B. Les deux ateliers sont tellement éloignés que des chariots doivent être conduits d'un atelier à l'autre.
    </li>
    <li>
      Processus : La production au sein d'une équipe ou d'un atelier n'est pas utile pour le client. Par exemple, une pièce est produite avec une dimension erronée, ce qui oblige le client à la jeter ou à la traiter. On rencontrera ce genre de problème quand le besoin du client n'est pas bien compris (ou décrit). Il est nécessaire d'établir d'une manière précise les besoins du client pour choisir les étapes du processus de production.
    </li>
    <li>
      Stock : C'est souvent le résultat d'un phénomène de surproduction. Cela implique des stocks d'objets inutilisables (les produits s'abîment ou sont périmés). L'approvisionnement est peut-être à revoir pour éviter une quantité trop importante et donc difficilement gérable. Si les stocks sont nécessaires comme dans le cas d'un entrepôt de logistique, c'est peut-être le système de stockage qui nécessite une amélioration.
    </li>
    <li>
      Mouvement : À ne pas confondre avec le transport. Ici, le gaspillage implique qu'une personne (ou une machine) est dans l'obligation de faire des mouvements difficiles pour atteindre outils, matières, etc.
    </li>
    <li>
      Défauts : Un problème de qualité courant qui survient à la suite d'un processus de production mal contrôlé ou de mauvaises transmissions d'indications.
    </li>
  </ul>
  Notez qu'un type supplémentaire de Muda peut parfois être ajouté. Mais vous pouvez probablement vous concentrer sur ces 7 types pour commencer.
  <div class="ia-sensei-quote">
    <span class="quote">"${WASTE}"</span>
    <br>
    <span class="author">${WASTE_SOURCE}</span>
  </div>
</p>
`;

const SPOT_PB_TITLE = 'Repérer un problème';

export default {
  SPOT_PB,
  SPOT_PB_TITLE,
  WHAT_IS_A_PB,
};
